// LEGACY (pre scaling Phase 1) — copy of main as of 2026-10-27, served to
// workspaces WITHOUT the `social_graph_v2` feature flag (see ../SocialGraph.tsx).
// When adding a feature to the v2 page, ask whether the legacy version needs it
// too (it stays in production until v2 is confirmed), then apply it here as well
// if so. Delete the whole `legacy/` folder once the flag is retired.
import { actorConnectionStats } from './buildGraph';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useRegisterEvents,
  useSetSettings,
  useSigma,
} from '@react-sigma/core';
import {
  EdgeAttributes,
  edgeVolumeSize,
  GraphHighlight,
  matchesHighlight,
  NodeAttributes,
  TooltipState,
} from '../graphTypes';
import {
  categoryHighlightColor,
  CONTEXT_EDGE_MUTE,
  CONTEXT_NODE_MUTE,
  DIMMED_NODE_COLOR,
  mixWithBackground,
  SCOPE_INBOUND_COLOR,
  SCOPE_OUTBOUND_COLOR,
  zscoreEdgeStyle,
} from '../graphColors';
import { createDrawNodeLabel } from '../drawNodeLabel';
import { FALLBACK_PLATFORM_ICON, PLATFORM_ICONS } from '../platformIcons';
import { platformNamesMapping } from '../../../utils/platforms';
import styles from '../SocialGraphPOC.module.scss';

// How long the cursor must rest on a node before the rest of the graph dims.
// Sweeping across nodes never triggers the dim (each leave cancels the timer),
// so exploration doesn't strobe; a deliberate pause reads as intent.
const HOVER_DIM_DELAY_MS = 300;

// While an actor is selected/dwelled, ITS edges scale thickness by interaction
// volume (investigator story: the strongest ties should be the most obvious).
// The formula is shared with buildGraph's resting sizes (graphTypes.ts), so
// with VOLUME_SIZED_RESTING_EDGES on, selecting a node changes color/arrows
// but never thickness — no jarring resize on click.

export const GraphHoverEffects: React.FC<{
  focusedNode: string | null;
  showKeyConnectors: boolean;
  onFocusNode: (nodeId: string | null) => void;
  activeEdgeKeys: Set<string> | null;
  activeNodeIds: Set<string> | null;
  brushNodeIds: Set<string> | null;
  highlight: GraphHighlight | null;
  // z-score analysis active: anomaly styling outranks the in/out recolor on the
  // active node's edges
  zscoreEdgeColoring: boolean;
}> = ({
  focusedNode,
  showKeyConnectors,
  onFocusNode,
  activeEdgeKeys,
  activeNodeIds,
  brushNodeIds,
  highlight,
  zscoreEdgeColoring,
}) => {
  const sigma = useSigma<NodeAttributes, EdgeAttributes>();
  const registerEvents = useRegisterEvents();
  const setSettings = useSetSettings<NodeAttributes, EdgeAttributes>();
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // hover only engages the graph-wide dim after the cursor DWELLS on a node —
  // instant dimming made the whole graph flicker while users swept across it
  // (user complaint). Tooltip + cursor stay immediate via hoveredNode; only the
  // reducers wait for dwelledNode. Leaving clears both at once.
  const [dwelledNode, setDwelledNode] = useState<string | null>(null);
  const dwellTimerRef = useRef<number | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const activeNodeRef = useRef<string | null>(null);

  const activeNode = dwelledNode ?? focusedNode;

  const updateTooltipPosition = useCallback(() => {
    const n = activeNodeRef.current;
    if (!n) return;
    const display = sigma.getNodeDisplayData(n);
    if (!display) return;
    const { x, y } = sigma.framedGraphToViewport({
      x: display.x,
      y: display.y,
    });
    setTooltip((prev) => (prev ? { ...prev, x, y } : prev));
  }, [sigma]);

  useEffect(() => {
    registerEvents({
      enterNode: (event) => {
        setHoveredNode(event.node);
        if (dwellTimerRef.current !== null)
          window.clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = window.setTimeout(() => {
          setDwelledNode(event.node);
        }, HOVER_DIM_DELAY_MS);
      },
      leaveNode: () => {
        setHoveredNode(null);
        if (dwellTimerRef.current !== null) {
          window.clearTimeout(dwellTimerRef.current);
          dwellTimerRef.current = null;
        }
        setDwelledNode(null);
      },
      afterRender: updateTooltipPosition,
      clickNode: (event) =>
        onFocusNode(event.node === focusedNode ? null : event.node),
      clickStage: () => onFocusNode(null),
    });
  }, [registerEvents, updateTooltipPosition, focusedNode, onFocusNode]);

  // don't let a pending dwell timer fire after unmount
  useEffect(
    () => () => {
      if (dwellTimerRef.current !== null)
        window.clearTimeout(dwellTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // only consume Escape when a node is actually pinned — otherwise let it
      // bubble to an enclosing overlay's close handler
      if (e.key === 'Escape' && focusedNode !== null) {
        e.preventDefault();
        onFocusNode(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onFocusNode, focusedNode]);

  useEffect(() => {
    sigma.getContainer().style.cursor = hoveredNode ? 'pointer' : '';
  }, [hoveredNode, sigma]);

  useEffect(() => {
    if (!focusedNode) return;
    const graph = sigma.getGraph();
    if (!graph.hasNode(focusedNode)) return;
    // Convert the node's stored (layout) coordinates to framed coordinates
    // ourselves instead of react-sigma's gotoNode, which reads
    // sigma.getNodeDisplayData(). That cache is re-indexed synchronously with
    // RAW coordinates on every sigma.setSetting() call and only re-normalized
    // on the next animation frame — and the reducer effect below calls
    // setSettings on every hover change and every timeline playback tick. A
    // click landing in that window used to fly the camera to a raw layout
    // coordinate (hundreds of framed units away): the graph vanished and only
    // the empty canvas remained until a reset.
    const { x, y } = graph.getNodeAttributes(focusedNode);
    // never animate the camera toward a non-finite coordinate: sigma's
    // validateState only checks `typeof === 'number'`, which NaN passes, so a
    // bad target poisons the camera state (graph shrinks into a corner or the
    // canvas goes blank) until a reset/reload
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      console.warn(`Social graph: node ${focusedNode} has non-finite coordinates — camera move skipped`);
      return;
    }
    const target = sigma.viewportToFramedGraph(sigma.graphToViewport({ x, y }));
    void sigma.getCamera().animate(target, { duration: 600 });
  }, [focusedNode, sigma]);

  useEffect(() => {
    const drawFn = createDrawNodeLabel(showKeyConnectors);
    setSettings({ defaultDrawNodeLabel: drawFn, defaultDrawNodeHover: drawFn });
  }, [showKeyConnectors, setSettings]);

  useEffect(() => {
    if (focusedNode && brushNodeIds && !brushNodeIds.has(focusedNode)) {
      onFocusNode(null);
    }
  }, [focusedNode, brushNodeIds, onFocusNode]);

  useEffect(() => {
    const graph = sigma.getGraph();
    const hasTimeFilter = activeEdgeKeys !== null;

    if (!activeNode && !hasTimeFilter && !highlight) {
      setSettings({
        nodeReducer:
          showKeyConnectors || focusedNode
            ? (n, data) => {
                const { isKeyConnector } = data as unknown as {
                  isKeyConnector?: boolean;
                };
                const showLabel =
                  (showKeyConnectors && !!isKeyConnector) ||
                  n === focusedNode;
                return showLabel ? { ...data, forceLabel: true } : data;
              }
            : null,
        edgeReducer: null,
      });
    } else {
      const neighbors = activeNode
        ? new Set(graph.neighbors(activeNode))
        : null;

      setSettings({
        nodeReducer: (n, data) => {
          if (activeNodeIds && !activeNodeIds.has(n)) {
            return { ...data, hidden: true };
          }
          const { isKeyConnector } = data as unknown as {
            isKeyConnector?: boolean;
          };
          const connectorLabel =
            showKeyConnectors && !!isKeyConnector;
          if (activeNode) {
            if (n === activeNode) {
              return {
                ...data,
                zIndex: 1,
                forceLabel: true,
                highlighted: false,
              };
            }
            if (neighbors!.has(n)) {
              return {
                ...data,
                zIndex: 1,
                forceLabel: connectorLabel || n === focusedNode,
                highlighted: false,
              };
            }
            // non-neighbors stay visible with their hue muted — the surrounding
            // communities are the context the user reads the selection against
            // (fully hiding them shipped once; users lost the graph's structure).
            // label: null also skips the piggybacked connector ring — fine,
            // these nodes used to be hidden entirely
            return {
              ...data,
              color: mixWithBackground(data.color, CONTEXT_NODE_MUTE),
              zIndex: 0,
              label: null,
              forceLabel: false,
            };
          }
          if (highlight) {
            // community/role spotlight from the panel: dim non-members toward
            // the background
            // a facet-dimmed node ("Highlight instead of hide") stays gray
            // context even when it belongs to the spotlighted group
            if (
              !matchesHighlight(data as unknown as NodeAttributes, highlight) ||
              (data as unknown as NodeAttributes).filterDimmed
            ) {
              return {
                ...data,
                color: DIMMED_NODE_COLOR,
                zIndex: 0,
                forceLabel: false,
              };
            }
            // category spotlights repaint members in the red/blue signal hue
            // (the legend mirrors it); community/z-score spotlights keep
            // members' own colors
            const categoryColor = categoryHighlightColor(highlight);
            if (categoryColor) {
              return {
                ...data,
                color: categoryColor,
                zIndex: 1,
                forceLabel: connectorLabel || n === focusedNode,
              };
            }
          }
          // time filter or spotlight active, no hover
          return {
            ...data,
            zIndex: 1,
            forceLabel: connectorLabel || n === focusedNode,
          };
        },
        edgeReducer: (edge, data) => {
          const [source, target] = graph.extremities(edge);
          if (activeEdgeKeys && !activeEdgeKeys.has(`${source}-${target}`)) {
            return { ...data, hidden: true };
          }
          if (activeNode) {
            if (source === activeNode || target === activeNode) {
              // reveal even edges hidden by the cross-cluster mode — the active
              // node's long-range links are exactly what the user is inspecting —
              // but NOT edges the relation-type filter removed on purpose.
              // In z-score analysis anomaly outranks direction: anomalous edges
              // keep their stored red/orange anomaly color (designer spec)
              // Active edges: thickness = interaction volume, and the arrowed
              // program is forced so direction stays readable even when the
              // global "Direction arrows" switch is off
              if (
                zscoreEdgeColoring &&
                zscoreEdgeStyle(data.zScore).anomalous
              ) {
                return {
                  ...data,
                  size: edgeVolumeSize(data.totalCount),
                  type: 'curvedArrow',
                  zIndex: 2,
                  hidden: data.typeFiltered === true,
                };
              }
              // Direction reuses the scope-flow pair: orange = the node mentions
              // someone (outgoing), cyan = someone mentions it (incoming)
              const color =
                source === activeNode
                  ? SCOPE_OUTBOUND_COLOR
                  : SCOPE_INBOUND_COLOR;
              return {
                ...data,
                color,
                size: edgeVolumeSize(data.totalCount),
                type: 'curvedArrow',
                zIndex: 2,
                hidden: data.typeFiltered === true,
              };
            }
            // keep a faint trace of the edge's own hue instead of a flat
            // near-background gray — inter-community links stay legible
            return {
              ...data,
              color: mixWithBackground(data.color, CONTEXT_EDGE_MUTE),
              zIndex: 0,
            };
          }
          if (highlight) {
            const sourceAttrs = graph.getNodeAttributes(source);
            const targetAttrs = graph.getNodeAttributes(target);
            const bothMatch =
              !sourceAttrs.filterDimmed &&
              !targetAttrs.filterDimmed &&
              matchesHighlight(sourceAttrs, highlight) &&
              matchesHighlight(targetAttrs, highlight);
            return bothMatch
              ? { ...data, zIndex: 1 }
              : {
                  ...data,
                  color: mixWithBackground(data.color, CONTEXT_EDGE_MUTE),
                  zIndex: 0,
                };
          }
          return data;
        },
      });
    }

  }, [
    activeNode,
    focusedNode,
    sigma,
    setSettings,
    showKeyConnectors,
    activeEdgeKeys,
    activeNodeIds,
    highlight,
    zscoreEdgeColoring,
  ]);

  // Tooltip is hover-immediate (independent of the dwell-delayed dim);
  // suppress it when the node is already open in the side panel. Kept apart
  // from the reducer effect above on purpose: that one re-runs on every
  // timeline playback tick, and rebuilding the tooltip state there scheduled
  // a React update on every tick — after 50 consecutive ticks React logs
  // "Maximum update depth exceeded" (its passive-effect heuristic), blamed on
  // whichever update came next. The position is refreshed by afterRender.
  useEffect(() => {
    activeNodeRef.current = hoveredNode;
    if (!hoveredNode || hoveredNode === focusedNode) {
      setTooltip(null);
      return;
    }

    const graph = sigma.getGraph();
    const attrs = graph.getNodeAttributes(hoveredNode);

    const mentionCounts: Record<string, number> = {};
    graph.forEachEdge(hoveredNode, (_edge, edgeAttrs) => {
      for (const [type, count] of Object.entries(edgeAttrs.mentionTypes)) {
        mentionCounts[type] = (mentionCounts[type] ?? 0) + count;
      }
    });

    // from the stored coordinates, not getNodeDisplayData(): the setSettings
    // call just above left that cache holding raw coordinates until the next
    // frame (see the camera effect) — afterRender then keeps it in sync
    const pos = sigma.graphToViewport({ x: attrs.x, y: attrs.y });

    setTooltip({
      x: pos.x,
      y: pos.y,
      nodeId: hoveredNode,
      label: attrs.label,
      inEvent: attrs.inEvent,
      community: attrs.community,
      betweenness: attrs.betweenness,
      pagerank: attrs.pagerank,
      hubScore: attrs.hubScore,
      authorityScore: attrs.authorityScore,
      actorRole: attrs.actorRole,
      platform: attrs.platform,
      sourceUrl: attrs.sourceUrl,
      connections: actorConnectionStats(graph, hoveredNode),
      mentionCounts,
      isKeyConnector: attrs.isKeyConnector,
      isHighRisk: attrs.isHighRisk,
      isStateAffiliated: attrs.isStateAffiliated,
      isHighInteraction: attrs.isHighInteraction,
      reciprocity: attrs.reciprocity,
      clusteringCoeff: attrs.clusteringCoeff,
      platformDiversity: attrs.platformDiversity,
      bridgeScore: attrs.bridgeScore,
      weightGini: attrs.weightGini,
      avgZScore: attrs.avgZScore,
      weightedAnomalyScore: attrs.weightedAnomalyScore,
    });
  }, [hoveredNode, focusedNode, sigma]);

  if (!tooltip) return null;

  // hover tooltip is deliberately minimal — name + platform; everything else
  // lives in the Information panel that opens on click
  const TooltipPlatformIcon =
    PLATFORM_ICONS[tooltip.platform] ?? FALLBACK_PLATFORM_ICON;
  const tooltipPlatformLabel =
    platformNamesMapping[tooltip.platform] ?? tooltip.platform;

  return (
    <div className={styles.tooltip} style={{ left: tooltip.x, top: tooltip.y }}>
      <div className={styles.tooltipHeader}>
        {tooltip.platform && (
          <span
            className={styles.nodePanelPlatform}
            role="img"
            aria-label={tooltipPlatformLabel}
          >
            <TooltipPlatformIcon aria-hidden="true" />
          </span>
        )}
        <p className={styles.tooltipTitle}>{tooltip.label}</p>
      </div>
    </div>
  );
};
